/**
 * Product Line Reclassification (Young's initiative, Max 2026-08-23).
 *
 * Two tabs, because the audiences differ: HINC reads the codes that are already settled, the Product
 * Line team works the ones still open. No paging — Max asked for the whole list, so the table scrolls
 * inside its own box with the header frozen, and sorting and the Excel-style column filters come from
 * the Sample Tracker's own helpers rather than a second implementation.
 *
 * What this screen deliberately does NOT do yet: the new classification is applied on no other screen
 * or report, and the ten overlapping rules are unresolved — so the open tab shows the questions instead
 * of the portal inventing answers.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Database, AlertTriangle, Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import i18n from './i18n';
import Header from './Header';
import TabIcon from './TabIcon';
import ModuleHelp from './ModuleHelp';
import useAuth from './useAuth';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import { BACKEND_URL } from './backendConfig';
import useFrozenColumns from './useFrozenColumns';
import useRowWindow from './useRowWindow';
import { fmtDateTimeVN } from './timeZone';
import './SPHub.css';
import './ProductLine.css';

const API = `${BACKEND_URL}/api/sp/product-line`;
// The generic xlsx endpoint the Sample Tracker exposes; Ticket Codes reuses it the same way.
const XLSX_URL = `${BACKEND_URL}/api/development/sample-tracker/export-xlsx`;

const TAG_LIST = ['tagDependent', 'tagStandalone', 'tagOneOff', 'tagCollab', 'tagB2b', 'tagMarketing'];
const TAG_KEYS = new Set(TAG_LIST);
// "Not filled in yet" means no tag at all — an item with even one tag has been looked at.
const hasNoTag = (r) => TAG_LIST.every((k) => !r[k]);
/* SAP's material types, in the words the Product Line team uses. The codes are German abbreviations
   and nobody outside SAP reads them: FERT = Fertigerzeugnis, HAWA = Handelsware, and so on. */
/* The words are dictionary keys (src/locales/xPl.js) — the four-letter codes stay SAP's. */
const MATERIAL_TYPE = {
  FERT: 'xPl.mt.fert',
  HAWA: 'xPl.mt.hawa',
  UNBW: 'xPl.mt.unbw',
  DIEN: 'xPl.mt.dien',
};
/* A column's `get` takes an optional translate function: the screen's getters pass its own `t`, so a memo
   keyed on `t` re-sorts when the language changes; every other caller gets the shared instance. */
const tShared = (key) => i18n.t(key);
const yn = (v) => (v ? 'Y' : '');

/* Column order copied from the CEO's workbook so the export reads the way HINC already reads it, with
   SAP code added right after the selling SKU (Max, 2026-08-23) — they are different identifiers:
   '10051R1' in PLM is 10001555 in SAP, and the portal keys everything on the SAP one. */
// The ones that stay put while the rest scroll: without them a row 15 columns to the right is an
// anonymous set of codes (Max, 2026-08-23). Four since the colour joined the style name (2026-10-12):
// a style is several materials, and the colour is what tells them apart. The mapped table pins a
// No. column ahead of them and so passes FROZEN + 1 — the count is of CELLS in the pinned run.
const FROZEN = 4;
/* The material's colour, beside the style it belongs to — Max, 2026-10-12: "thêm cột color vào sau
   cột style name nhé". The style name repeats down every colour of a style; without this the rows
   of one style were told apart only by their codes. */
const COLOR_COLUMN = { key: 'color', labelKey: 'xPl.col.color', w: 16, px: 130, get: (r) => r.color || '' };
const COLUMNS = [
  /* WHERE THERE IS NO STYLE, THE MATERIAL'S OWN NAME — Miley via Max, 2026-10-06: "sao nhiều sku
     đầu 6 & 9 không hiển thị Style name. E có đang ẩn đi dữ liệu nào k?"

     Nothing was hidden. Measured: every one of the 89 materials starting 6 and the 129 starting 9
     has no style name, and they are exactly the UNBW (non-valuated) and DIEN (service) rows — a
     service and a giveaway never go through the style master, so there is no style to name. The
     crosswalk agrees: 0 rows for the 6 prefix, 6 for the 9 prefix.

     But their OWN name was in the payload all along, as `itemName`, and this screen never drew it
     — so 218 rows read as anonymous while the thing that identifies them sat one field away.

     A FALLBACK AND NOT A SECOND COLUMN, because the two names are the same string on 8,803 of
     9,079 rows and differ on 55; a column would have duplicated the one beside it for 97% of the
     table. The cell MARKS the fallback so nobody reads a material name as a style, and `get`
     returns it so the search and the Excel export can both find the row. */
  { key: 'styleName',
    labelKey: 'xPl.col.styleName',
    hintKey: 'xPl.col.styleNameHint',
    w: 34,
    px: 240,
    get: (r) => r.styleName || r.itemName || '',
    cell: (r) => (r.styleName ? r.styleName
      : (r.itemName
        ? (
          <span className="pl-itemname" title={i18n.t('xPl.col.itemNameTitle')}>
            {r.itemName}
          </span>
        )
        : '—')) },
  COLOR_COLUMN,
  { key: 'sellingSku', labelKey: 'xPl.col.sellingSku', w: 13, px: 100, mono: true, get: (r) => r.sellingSku || '' },
  { key: 'sapCode', labelKey: 'xPl.col.sapCode', w: 13, px: 100, mono: true, get: (r) => r.sapCode || '' },
  /* What KIND of material this is — Max, 2026-09-07: "cho bảng hiện thêm loại còn lại nhé". The tab
     can list all 8,835 now, and without this the trading goods are indistinguishable from the
     finished goods sitting next to them. SAP's four-letter type is spelled out, because FERT and
     HAWA mean nothing to the people reading this table — the raw code is kept in the tooltip so a
     row can still be matched against SAP. */
  { key: 'materialType',
    labelKey: 'xPl.col.materialType',
    w: 16,
    px: 128,
    hint: 'SAP MARA-MTART',
    get: (r, tr = tShared) => (MATERIAL_TYPE[r.materialType] ? tr(MATERIAL_TYPE[r.materialType]) : (r.materialType || '')) },
  { key: 'currentCode', labelKey: 'xPl.col.currentCode', w: 20, px: 150, mono: true, get: (r) => r.currentCode || '' },
  { key: 'newCode', labelKey: 'xPl.col.newCode', w: 20, px: 150, mono: true, get: (r) => r.newCode || '' },
  /* The brand a material moves to — Max, 2026-09-07. Until now only the LINE could be reclassified
     and the brand was whatever SAP's hierarchy said, which is why TERG could not be lifted out of
     11/Helinox from in here: brand 41 does not exist in SAP at all. With nothing said these repeat
     the two SAP columns they sit beside, because "những cái còn lại ko đổi".

     Code then new code, name then new name — the same shape the line block below already has, so a
     reader compares across one column rather than across four ("cột New Hierarchy Brand Code sau
     cột Hierarchy Brand Code"). */
  /* THE NAMES MILEY WRITES ON THEM — via Max, 2026-09-17: "e đổi tên mấy cột này trong Product
     Line... Theo tên c mới nhập vào ô vàng á", on her own header strip. The six NAME columns take
     the words the people who fill this in already use — Current Brand, New Brand, Current Line,
     New Line, Category, Sub Category — and the CODE columns keep theirs, because a code is what
     SAP calls it and is matched against SAP by that name.
     Labels only: `key` is what the sort, the funnels and the saved column state run on. */
  { key: 'brandCode', labelKey: 'xPl.col.brandCode', w: 9, px: 78, mono: true, get: (r) => r.brandCode || '' },
  { key: 'newBrandCode', labelKey: 'xPl.col.newBrandCode', w: 11, px: 100, mono: true, get: (r) => r.newBrandCode || '' },
  { key: 'brandName', labelKey: 'xPl.col.brandName', w: 14, px: 104, get: (r) => r.brandName || '' },
  { key: 'newBrandName', labelKey: 'xPl.col.newBrandName', w: 14, px: 112, get: (r) => r.newBrandName || '' },
  { key: 'lineCode', labelKey: 'xPl.col.lineCode', w: 9, px: 78, mono: true, get: (r) => r.lineCode || '' },
  { key: 'newLineCode', labelKey: 'xPl.col.newLineCode', w: 11, px: 96, mono: true, get: (r) => r.newLineCode || '' },
  { key: 'lineName', labelKey: 'xPl.col.lineName', w: 18, px: 132, get: (r) => r.lineName || '' },
  { key: 'newLineName', labelKey: 'xPl.col.newLineName', w: 24, px: 176, get: (r) => r.newLineName || '' },
  { key: 'categoryCode', labelKey: 'xPl.col.categoryCode', w: 11, px: 96, mono: true, get: (r) => r.categoryCode || '' },
  { key: 'categoryName', labelKey: 'xPl.col.category', w: 18, px: 148, get: (r) => r.categoryName || '' },
  { key: 'subCategoryCode', labelKey: 'xPl.col.subCategoryCode', w: 12, px: 108, mono: true, get: (r) => r.subCategoryCode || '' },
  { key: 'subCategoryName', labelKey: 'xPl.col.subCategoryName', w: 26, px: 196, get: (r) => r.subCategoryName || '' },
  /* The workbook labels these in Korean; the portal is read in English by the people who fill them
     in (Max, 2026-08-25). The Korean stays in the header's tooltip so a row can still be matched
     against the CEO's file. */
  { key: 'tagDependent', labelKey: 'xPl.col.tagDependent', hint: '종속부속', w: 12, px: 92, get: (r) => yn(r.tagDependent) },
  { key: 'tagStandalone', labelKey: 'xPl.col.tagStandalone', hint: '단독액세서리', w: 14, px: 108, get: (r) => yn(r.tagStandalone) },
  { key: 'tagOneOff', labelKey: 'xPl.col.tagOneOff', w: 9, px: 74, get: (r) => yn(r.tagOneOff) },
  { key: 'tagCollab', labelKey: 'xPl.col.tagCollab', w: 13, px: 92, get: (r) => yn(r.tagCollab) },
  { key: 'tagB2b', labelKey: 'xPl.col.tagB2b', w: 7, px: 62, get: (r) => yn(r.tagB2b) },
  { key: 'tagMarketing', labelKey: 'xPl.col.tagMarketing', hint: '마케팅활용', w: 12, px: 92, get: (r) => yn(r.tagMarketing) },
];

/* The same sheet: the column that explains why these rows are here — they are not finished goods;
   HAWA is trading goods, UNBW non-valuated, DIEN a service — is already in it. This list used to add
   Material type a SECOND time, from before COLUMNS carried it (2026-09-07): two headers, one funnel
   key and the column twice in the export (seen 2026-10-12). One sheet, so one list. */
const OTHER_COLUMNS = COLUMNS;

const DECISION_COLUMNS = [
  { key: 'parts', labelKey: 'xPl.col.parts', mono: true, px: 190, w: 26, get: (r) => `${r.brand_code}/${r.old_line_code}/${r.category_code}/${r.sub_category}` },
  { key: 'style_name', labelKey: 'xPl.col.style', px: 300, w: 42, get: (r) => r.style_name || '' },
  { key: 'candidates', labelKey: 'xPl.col.candidates', px: 150, w: 18, get: (r) => r.candidates || '' },
  { key: 'materials', labelKey: 'xPl.col.materials', num: true, px: 100, w: 11, get: (r) => r.materials },
  { key: 'fert', labelKey: 'xPl.col.sellable', num: true, px: 95, w: 10, get: (r) => r.fert },
  { key: 'sample', labelKey: 'xPl.col.example', mono: true, px: 320, w: 34, get: (r) => `${r.sample_sku} · ${r.sample_name || ''}` },
];
/* THE RULES THEMSELVES — Miley via Max, 2026-10-06: "chị cho em xin rule để em update á chứ hem
   chỉnh sửa dc ạ", then "Để mở hết toàn bộ option thay vì là suggested candidates".

   Correcting a ROW was already possible and is the wrong unit: /line/:sku writes a style override,
   which leaves the RULE still wrong, so the next import brings the same question back. The two HXW
   rule rows added this morning cleared 26 materials at once and are what this tab is for.

   MATERIALS IS A COLUMN, not a detail. The difference between a rule answering 581 materials and
   one answering none is the whole question somebody opens this tab to see. */
const RULE_COLUMNS = [
  { key: 'brandCode', labelKey: 'xPl.col.brand', w: 9, px: 72, mono: true, get: (r) => r.brandCode || '' },
  { key: 'brandName', labelKey: 'xPl.col.brandNameRule', w: 14, px: 120, get: (r) => r.brandName || '' },
  { key: 'oldLineCode', labelKey: 'xPl.col.oldLine', w: 9, px: 72, mono: true, get: (r) => r.oldLineCode || '' },
  /* THE NAME BESIDE EACH CODE — Max, 2026-10-06: "cho thêm cột name cho 3 cột này để dễ xem nhé".
     Brand and the new line already carried one; these three did not, so half the row could not be
     read without SAP open beside it. A sub-category of 'Empty' has no name because there is no
     sub-category — the dash already says so, and 102 of the 328 rules are in that case. */
  { key: 'oldLineName', labelKey: 'xPl.col.oldLineName', w: 14, px: 120, get: (r) => r.oldLineName || '' },
  { key: 'categoryCode', labelKey: 'xPl.col.category', w: 10, px: 84, mono: true, get: (r) => r.categoryCode || '' },
  { key: 'categoryName', labelKey: 'xPl.col.categoryNameRule', w: 14, px: 118, get: (r) => r.categoryName || '' },
  { key: 'subCategory', labelKey: 'xPl.col.subCategory', w: 11, px: 96, mono: true, get: (r) => r.subCategory || '' },
  { key: 'subCategoryName', labelKey: 'xPl.col.subCategoryNameRule', w: 16, px: 150, get: (r) => r.subCategoryName || '' },
  { key: 'newLineCode', labelKey: 'xPl.col.newLine', w: 9, px: 80, mono: true, get: (r) => r.newLineCode || '' },
  { key: 'newLineName', labelKey: 'xPl.col.newLineNameRule', w: 15, px: 130, get: (r) => r.newLineName || '' },
  /* THE BRAND A RULE NAMES, and most name none — Miley via Max, 2026-10-06. Until today the brand
     always followed the new line, so a TERG product moved onto Travel lost brand 41 the moment it
     left the TERG line; she needs the brand keyed on where a material comes FROM. Blank is the old
     behaviour and is the common case, so it prints a dash rather than nothing: an empty cell reads
     as missing data, and this is an answer. */
  { key: 'newBrandCode',
    labelKey: 'xPl.col.newBrand',
    hintKey: 'xPl.col.newBrandHint',
    w: 10, px: 88, mono: true,
    get: (r) => r.newBrandCode || '' },
  { key: 'newBrandName', labelKey: 'xPl.col.newBrandNameRule', w: 14, px: 118, get: (r) => r.newBrandName || '' },
  { key: 'materials', labelKey: 'xPl.col.materials', num: true, w: 10, px: 92, get: (r) => r.materials },
  { key: 'sellable', labelKey: 'xPl.col.sellable', num: true, w: 9, px: 84, get: (r) => r.sellable },
  /* A rule the workbook could not answer still carries what it offered. Kept visible: it is the
     reason the rule is open, and the thing Miley is overriding when she sets a line. */
  { key: 'candidates', labelKey: 'xPl.col.candidates', w: 13, px: 110, get: (r) => r.candidates || '' },
  { key: 'source', labelKey: 'xPl.col.source', w: 15, px: 130, get: (r) => r.source || '' },
  { key: 'updatedBy', labelKey: 'xPl.col.updatedBy', w: 12, px: 100, get: (r) => r.updatedBy || '' },
];

/* The per-material list on the decisions tab. The same first columns as the "no rule" tables — Max
   asked for it in that shape (2026-09-03) — with Candidates in place of the new-code columns, which
   are empty here by definition: that is the whole point of the tab. */
const DECIDE_ITEM_COLUMNS = [
  { key: 'styleName', labelKey: 'xPl.col.styleName', w: 34, px: 240, get: (r) => r.styleName || '' },
  COLOR_COLUMN,
  { key: 'sellingSku', labelKey: 'xPl.col.sellingSku', w: 13, px: 100, mono: true, get: (r) => r.sellingSku || '' },
  { key: 'sapCode', labelKey: 'xPl.col.sapCode', w: 13, px: 100, mono: true, get: (r) => r.sapCode || '' },
  { key: 'itemName', labelKey: 'xPl.col.materialName', w: 34, px: 250, get: (r) => r.itemName || '' },
  { key: 'materialType', labelKey: 'xPl.col.materialType', w: 12, px: 96, mono: true, get: (r) => r.materialType || '' },
  { key: 'currentCode', labelKey: 'xPl.col.currentCode', w: 20, px: 150, mono: true, get: (r) => r.currentCode || '' },
  { key: 'candidates', labelKey: 'xPl.col.candidates', w: 16, px: 130, mono: true, get: (r) => r.candidates || '' },
  { key: 'brandCode', labelKey: 'xPl.col.brandCode', w: 9, px: 78, mono: true, get: (r) => r.brandCode || '' },
  { key: 'lineCode', labelKey: 'xPl.col.lineCode', w: 9, px: 78, mono: true, get: (r) => r.lineCode || '' },
  { key: 'lineName', labelKey: 'xPl.col.lineName', w: 18, px: 132, get: (r) => r.lineName || '' },
  { key: 'categoryCode', labelKey: 'xPl.col.categoryCode', w: 11, px: 96, mono: true, get: (r) => r.categoryCode || '' },
  { key: 'subCategoryCode', labelKey: 'xPl.col.subCategoryCode', w: 12, px: 108, mono: true, get: (r) => r.subCategoryCode || '' },
];

/* Two blocks of columns that are not like the rest, tinted so the eye finds them without reading
   the header — Max, 2026-09-07: "Các cột new và tag nên format màu khác để User dễ phân biệt nhé".

   The NEW columns are what this screen is FOR: the classification a material moves to. The tags are
   the six ticks a person maintains by hand. Everything else is SAP's, and reads as the plain
   background it always did. */
const NEW_KEYS = new Set(['newCode', 'newBrandCode', 'newBrandName', 'newLineCode', 'newLineName']);
const colTone = (key) => (NEW_KEYS.has(key) ? 'pl-new-col' : (TAG_KEYS.has(key) ? 'pl-tag-col' : ''));

// One question is one style on one code combination — the same key the answer is written under.
const decisionKey = (r) => `${r.style_name}|${r.brand_code}|${r.old_line_code}|${r.category_code}|${r.sub_category}`;

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`sp-stat ${tone || ''}`}>
      <div className="sp-stat-value">{value === undefined || value === null ? '—' : Number(value).toLocaleString('en-GB')}</div>
      <div className="sp-stat-label">{label}</div>
      {sub && <div className="sp-stat-sub">{sub}</div>}
    </div>
  );
}

/* One table body for both tabs: same sticky header, same Excel-style menus, different columns. Written
   once because the second copy is where the two would have drifted. */
/* `tagEdit` turns the six tag columns from a read-only Y into checkboxes, and adds the Action column
   that switches one row into edit mode (Max, 2026-08-25). It is optional: the two tabs that only
   ever show empty tags pass nothing and get exactly what they had. */
/* `action` is the general form of the Action column: { label, width, render(row) }. tagEdit stays as
   it is — it is the six checkboxes plus Edit/Save and nothing else would want that shape. The
   decisions tab needs a dropdown of candidates, so it passes this instead. */
function DataTable({ columns, rows, sort, setSort, colFilters, setColFilters, openFilter, setOpenFilter, allRows, keyOf, frozen = 0, tagEdit = null, action = null, numbered = false }) {
  const { t } = useTranslation();
  const getters = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c.get])), [columns]);
  /* MEASURED offsets, not the declared widths. Style Name is declared at 240px and renders at 327,
     so Selling SKU was pinned 87px too far left and sat on top of Style Name's right edge — which is
     where its sort caret and filter funnel live. Max, 2026-09-07: "cột style name bị mất nút sort và
     filter". Nothing was missing; it was underneath the next column. A width on a cell in an
     auto-layout table is a hint, and useFrozenColumns exists because this has cost time before. */
  const [tableRef, lefts] = useFrozenColumns(frozen, [columns, rows.length, numbered]);
  const stickyProps = (i) => (i < frozen
    ? { className: `sticky-col${i === frozen - 1 ? ' last-sticky' : ''}`, style: { left: lefts[i] || 0 } }
    : {});
  /* The row number is not a column of the data: it has no value to sort, filter or export, and
     giving it one would put "No." in the funnel menus and in the workbook. It is rendered here and
     counted in the frozen run, so `frozen` includes it. */
  const dataAt = (i) => (numbered ? i + 1 : i);

  /* Only the rows you can actually see are built — the mapped tab took 16 SECONDS to appear and its
     api had finished at 2.3. See useRowWindow for the whole story; the height is 35px, measured. */
  const ROW_H = 35;
  const [scrollBox, win] = useRowWindow({
    rowHeight: ROW_H,
    total: rows.length,
    resetKey: `${sort.key}|${sort.dir}|${Object.keys(colFilters).join(',')}`,
  });
  const { first, padTop, padBottom } = win;
  const visibleRows = win.windowed ? rows.slice(first, first + win.count) : rows;
  // Spans every column including the Action one, so it can never widen a real column.
  const spacerSpan = columns.length + (numbered ? 1 : 0) + (tagEdit || action ? 1 : 0);
  const thProps = (key) => ({
    sortKey: key,
    sort,
    onSort: (k) => setSort((p) => (p.key === k ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' })),
    filterValues: openFilter?.key === key ? distinctColumnValues(allRows, key, colFilters, getters) : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => setOpenFilter((p) => (p && p.key === k ? null : { key: k, anchor })),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, values, allValues) => {
      setColFilters((prev) => {
        const next = { ...prev };
        if (!values || !values.length || values.length === allValues.length) delete next[k];
        else next[k] = values;
        return next;
      });
      setOpenFilter(null);
    },
  });
  /* THE COLUMNS MUST NOT MOVE WHEN YOU SCROLL — Max, 2026-10-06: "width của các cột nên fix á chứ
     scroll xuống thì nó thay đổi width nhìn khó chịu".

     `useRowWindow` renders only the rows in view, and the table was auto-layout, so every column
     was sized from the content of whatever window happened to be on screen: scroll, the content
     changes, the columns resize under the reader. The declared `px` was only ever a hint.

     A colgroup plus `table-layout: fixed` makes it a measurement instead. The table's width is the
     EXACT SUM of its columns and not 100% — this file's own note records why: at 100% the layout
     hands the slack back out to every column and a declared 330 renders at 425. */
  const widths = [
    ...(numbered ? [54] : []),
    ...columns.map((c) => c.px || 120),
    ...(tagEdit ? [132] : []),
    ...(action ? [action.width || 210] : []),
  ];
  const tableWidth = widths.reduce((a, b) => a + b, 0);

  return (
    <div className="pl-scroll" ref={scrollBox}>
      <table
        className="pl-table pl-table-fixed"
        ref={tableRef}
        style={{ width: tableWidth, minWidth: tableWidth }}
      >
        <colgroup>
          {widths.map((w, i) => <col key={`c${i}`} style={{ width: w }} />)}
        </colgroup>
        <thead>
          <tr>
            {numbered && (
              <th className="pl-no-col num" style={{ ...(stickyProps(0).style || {}), width: 54, minWidth: 54 }}>
                {t('xPl.rowNo')}
              </th>
            )}
            {columns.map((c, i) => {
              const sp = stickyProps(dataAt(i));
              return (
                <SortTh
                  key={c.key}
                  label={t(c.labelKey)}
                  hint={c.hintKey ? t(c.hintKey) : c.hint}
                  className={[sp.className || '', colTone(c.key)].filter(Boolean).join(' ')}
                  style={{ ...(sp.style || {}), width: c.px, minWidth: c.px }}
                  {...thProps(c.key)}
                />
              );
            })}
            {tagEdit && <th className="pl-act-th" style={{ width: 132, minWidth: 132 }}>{t('xPl.action')}</th>}
            {action && (
              <th className="pl-act-th" style={{ width: action.width || 210, minWidth: action.width || 210 }}>
                {action.label || t('xPl.action')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {padTop > 0 && (
            <tr aria-hidden="true"><td colSpan={spacerSpan} style={{ height: padTop, padding: 0, border: 0 }} /></tr>
          )}
          {visibleRows.map((r, sliceIndex) => {
            // The number is the row's place in the whole list, not in the slice on screen.
            const rowIndex = first + sliceIndex;
            const editing = !!tagEdit && tagEdit.editingKey === keyOf(r);
            return (
            <tr key={keyOf(r)} className={editing ? 'pl-editing' : ''}>
              {numbered && (
                <td className={['pl-no-col', 'num', stickyProps(0).className || ''].filter(Boolean).join(' ')}
                  style={{ ...(stickyProps(0).style || {}), width: 54, minWidth: 54 }}
                >
                  {/* The position in what is on screen, so it counts the filtered list rather than
                      claiming a row is the 4,000th when nineteen are showing. */}
                  {rowIndex + 1}
                </td>
              )}
              {columns.map((c, i) => {
                const v = c.get(r);
                const sp = stickyProps(dataAt(i));
                return (
                  <td
                    key={c.key}
                    className={[c.mono ? 'mono' : '', c.num ? 'num' : '', TAG_KEYS.has(c.key) ? 'tag' : '', colTone(c.key), sp.className || ''].filter(Boolean).join(' ')}
                    style={{ ...(sp.style || {}), width: c.px, minWidth: c.px }}
                  >
                    {/* The two classification cells a person may correct. Both are style-level
                        answers, so the row's Save says how many materials they moved. */}
                    {c.key === 'newBrandCode' && editing && tagEdit.brands
                      ? (
                        <select
                          className="pl-line-pick"
                          value={tagEdit.draft.newBrandCode || ''}
                          onChange={(e) => tagEdit.setDraft({ ...tagEdit.draft, newBrandCode: e.target.value })}
                          aria-label={t('xPl.col.newBrandCode')}
                        >
                          <option value="">—</option>
                          {tagEdit.brands.map((b) => (
                            <option key={b.code} value={b.code}>{b.code} — {b.name}</option>
                          ))}
                        </select>
                      )
                      : c.key === 'newLineCode' && editing && tagEdit.lines
                      ? (
                        <select
                          className="pl-line-pick"
                          value={tagEdit.draft.newLineCode || ''}
                          onChange={(e) => tagEdit.setDraft({ ...tagEdit.draft, newLineCode: e.target.value })}
                          aria-label={t('xPl.col.newLineCode')}
                        >
                          <option value="">—</option>
                          {tagEdit.lines.map((l) => (
                            <option key={l.code} value={l.code}>{l.code} — {l.name}</option>
                          ))}
                        </select>
                      )
                      : TAG_KEYS.has(c.key)
                      ? (
                        <input
                          type="checkbox"
                          className="pl-tick"
                          /* Read-only rows still show a checkbox rather than a Y, so the column reads
                             the same whether or not you are editing it. */
                          checked={editing ? !!tagEdit.draft[c.key] : !!r[c.key]}
                          disabled={!editing}
                          onChange={(e) => tagEdit.setDraft({ ...tagEdit.draft, [c.key]: e.target.checked })}
                          aria-label={t(c.labelKey)}
                        />
                      )
                      /* A column may render its own cell. Only the Style Name one does today,
                         to mark a name that is the material's and not a style's. */
                      : c.cell ? c.cell(r, v)
                      : (v === '' || v === null || v === undefined ? '—' : v)}
                  </td>
                );
              })}
              {action && <td className="pl-act">{action.render(r)}</td>}
              {tagEdit && (
                <td className="pl-act">
                  {editing ? (
                    <>
                      <button type="button" className="sp-btn primary" disabled={tagEdit.saving}
                              onClick={() => tagEdit.onSave(r)}>
                        {tagEdit.saving ? t('xPl.saving') : t('xPl.save')}
                      </button>
                      <button type="button" className="sp-btn" disabled={tagEdit.saving} onClick={tagEdit.onCancel}>
                        {t('xPl.cancel')}
                      </button>
                    </>
                  ) : (
                    <button type="button" className="sp-btn" disabled={!tagEdit.canEdit || !!tagEdit.editingKey}
                            title={tagEdit.canEdit ? t('xPl.editTagsTitle') : t('xPl.noTagRights')}
                            onClick={() => tagEdit.onEdit(r)}>
                      <Pencil size={12} /> {t('xPl.edit')}
                    </button>
                  )}
                </td>
              )}
            </tr>
            );
          })}
          {padBottom > 0 && (
            <tr aria-hidden="true"><td colSpan={spacerSpan} style={{ height: padBottom, padding: 0, border: 0 }} /></tr>
          )}
          {!rows.length && <tr><td colSpan={spacerSpan}>{t('xPl.nothingMatches')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one). It walks the first tab (Ready to use); the tour switches back to it first. */
const TOUR = [
  { selector: '.pl-page .sp-stat-row', title: 'tourProductLine.statsT', body: 'tourProductLine.statsB' },
  { selector: '.pl-page .view-tabs', title: 'tourProductLine.tabsT', body: 'tourProductLine.tabsB' },
  { selector: '.pl-page .sp-toolbar', title: 'tourProductLine.toolbarT', body: 'tourProductLine.toolbarB' },
  { selector: '.pl-page .pl-table-fixed', title: 'tourProductLine.tableT', body: 'tourProductLine.tableB' },
];

const ProductLine = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('ready');
  const [summary, setSummary] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [mapped, setMapped] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  /* Reading this screen comes with sp_hub; CHANGING a tag is its own grant. Asked here as well as on
     the server so the Edit button is disabled with a reason, instead of failing at Save (Max,
     2026-08-25). */
  const { permissions } = useAuth();
  const mayEditTags = permissions?.product_line_tag === 'edit';
  /* Answering a question is the same right as editing the tags: both are a person classifying a
     product in the portal, and a second permission key for one dropdown would be one more thing to
     grant and forget. */
  const [decidePick, setDecidePick] = useState({});   // question key -> chosen line
  const [decideBusy, setDecideBusy] = useState(null); // question key being saved
  const [decideDone, setDecideDone] = useState('');   // what the last answer moved
  // "Show only what nobody has tagged yet" — the working list for whoever fills the six in.
  const [untaggedOnly, setUntaggedOnly] = useState(false);
  const [editKey, setEditKey] = useState('');     // SAP code of the row being edited
  const [draft, setDraft] = useState({});
  const [lineNote, setLineNote] = useState('');     // what the last line change moved
  const [savingTags, setSavingTags] = useState(false);
  // The item-level list behind "No rule yet". Fetched on first visit rather than with the page: it is
  // 2,104 sellable rows (6,124 with components), and most visits never open this tab.
  /* Every material behind the 140 questions, one row each. Loaded when the tab is opened rather than
     with the summary: it is 206 rows nobody needs until they are looking at this tab. */
  const [lines, setLines] = useState([]);            // pl_line_dim, for the line dropdown
  const [brands, setBrands] = useState([]);          // pl_brand_dim, for the brand dropdown
  const [rules, setRules] = useState(null);          // the rule table, loaded on first visit
  const [ruleSearch, setRuleSearch] = useState('');
  const [ruleSort, setRuleSort] = useState({ key: '', dir: 'asc' });
  const [ruleFilters, setRuleFilters] = useState({});
  const [ruleOpen, setRuleOpen] = useState(null);
  const [ruleEditId, setRuleEditId] = useState(null);   // rule being edited
  const [ruleDraftLine, setRuleDraftLine] = useState('');
  /* Held apart from the line so leaving the brand alone and leaving it EMPTY are the same
     keystroke — blank is a real answer here, meaning "follow the new line". */
  const [ruleDraftBrand, setRuleDraftBrand] = useState('');
  const [ruleAdd, setRuleAdd] = useState(null);          // the new-rule form, or null
  const [ruleMsg, setRuleMsg] = useState(null);
  const [ruleBusy, setRuleBusy] = useState(false);
  const [decideItems, setDecideItems] = useState(null);
  const [diSearch, setDiSearch] = useState('');
  const [diSort, setDiSort] = useState({ key: '', dir: 'asc' });
  const [diFilters, setDiFilters] = useState({});
  const [diOpen, setDiOpen] = useState(null);
  const [noRule, setNoRule] = useState(null);
  const [nrSearch, setNrSearch] = useState('');
  const [nrSort, setNrSort] = useState({ key: '', dir: 'asc' });
  const [nrFilters, setNrFilters] = useState({});
  const [nrOpen, setNrOpen] = useState(null);
  // The 4,020 that are not finished goods, in their own table under the sellable one (Max, 2026-08-24).
  const [other, setOther] = useState(null);
  const [otSearch, setOtSearch] = useState('');
  const [otSort, setOtSort] = useState({ key: '', dir: 'asc' });
  const [otFilters, setOtFilters] = useState({});
  const [otOpen, setOtOpen] = useState(null);
  const [dSort, setDSort] = useState({ key: '', dir: 'asc' });
  const [dFilters, setDFilters] = useState({});
  const [dOpen, setDOpen] = useState(null);
  /* Material type is the one column whose VALUE is translated, so a funnel ticked on it in one language
     would match nothing after a switch and empty the table without saying why. Dropped on a switch. */
  useEffect(() => {
    const drop = (setter) => setter((prev) => {
      if (!prev.materialType) return prev;
      const next = { ...prev };
      delete next.materialType;
      return next;
    });
    drop(setColFilters); drop(setNrFilters); drop(setOtFilters);
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // The whole mapped set in one call — Max asked for no paging, and 1,772 rows is a 300 KB payload.
      const [s, d, u, m, ln, br] = await Promise.all([
        fetch(`${API}/summary`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API}/decisions`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API}/unmapped`, { credentials: 'include' }).then((r) => r.json()),
        /* EVERY mapped material, not just the finished goods — Miley, 2026-09-07: "8835 mapped này
           là cái gì á, c thấy có 4078 SKUs mà nhỉ… còn lại phân nửa chưa làm hả". Nothing was
           missing; the tab asked for FERT and the other 4,757 are trading goods, non-valuated items
           and services. The card said 8,835, the table said 4,078, and nothing on screen explained
           the gap. It was briefly a dropdown; Max, the same day: "cho hiển chung 1 lần ko cần filter
           nhé" — the Material type column names every row, and the header funnel already filters by
           it, so a second control for the same job was one too many. */
        fetch(`${API}/rows?status=mapped&limit=20000&types=all`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API}/lines`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API}/brands`, { credentials: 'include' }).then((r) => r.json()),
      ]);
      if (s.error || m.error) throw new Error(s.error || m.error);
      setSummary(s); setDecisions(d.rows || []); setUnmapped(u.rows || []); setMapped(m.rows || []);
      setLines(ln.lines || []);
      setBrands(br.brands || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  /* One loader, two lists — a single toggled table hid the other half of the 6,124 behind a checkbox
     nobody ticked (Max, 2026-08-24). Both are fetched on the first visit to the tab. */
  const loadNoRule = useCallback(async (types, setRows) => {
    setError('');
    try {
      const d = await fetch(`${API}/rows?status=unmapped_code&limit=20000&types=${types}`,
        { credentials: 'include' }).then((r) => r.json());
      if (d.error) throw new Error(d.error);
      setRows(d.rows || []);
    } catch (e) { setError(e.message); }
  }, []);
  /* All material types: the questions cover HAWA and DIEN as well as finished goods, and a list that
     silently dropped them would not match the count on the tab. */
  const loadDecideItems = useCallback(async () => {
    try {
      const d = await fetch(`${API}/rows?status=needs_style_decision&limit=20000&types=all`,
        { credentials: 'include' }).then((r) => r.json());
      if (d.error) throw new Error(d.error);
      setDecideItems(d.rows || []);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => {
    if (tab === 'open' && decideItems === null) loadDecideItems();
  }, [tab, decideItems, loadDecideItems]);

  /* The rule table, on first visit to its tab. 328 rows with a material count each — cheap, but
     nobody opening "Ready to use" needs it. */
  const loadRules = useCallback(async () => {
    try {
      const d = await fetch(`${API}/rules`, { credentials: 'include' }).then((r) => r.json());
      if (d.error) throw new Error(d.error);
      setRules(d.rows || []);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => {
    if (tab === 'rules' && rules === null) loadRules();
  }, [tab, rules, loadRules]);

  const say = useCallback((text, bad) => {
    setRuleMsg({ text, bad });
    setTimeout(() => setRuleMsg(null), 6000);
  }, []);

  /* Saving a rule RELOADS the page's own counts as well as the rule list: a rule is the thing that
     moves materials between the three tabs, so leaving the tab badges on their old numbers would
     have the screen disagree with itself one click later. */
  const saveRule = useCallback(async (body) => {
    setRuleBusy(true);
    try {
      const d = await fetch(`${API}/rule`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => r.json());
      if (d.error) throw new Error(d.error);
      say(i18n.t(d.action === 'INSERT' ? 'xPl.rules.added' : 'xPl.rules.updated', { n: d.materials }));
      setRuleEditId(null); setRuleAdd(null);
      setRules(null); setNoRule(null); setOther(null); setDecideItems(null);
      await Promise.all([loadRules(), load()]);
    } catch (e) { say(e.message, true); }
    setRuleBusy(false);
  }, [say, loadRules, load]);

  const dropRule = useCallback(async (r) => {
    /* The count is on the row already, so the warning can be exact before anything is sent. */
    if (r.materials > 0 && !window.confirm(i18n.t('xPl.rules.confirmRemove', { n: r.materials }))) return;
    setRuleBusy(true);
    try {
      const d = await fetch(`${API}/rule/${r.ruleId}${r.materials > 0 ? '?force=1' : ''}`,
        { method: 'DELETE', credentials: 'include' }).then((x) => x.json());
      if (d.error) throw new Error(d.error);
      say(i18n.t('xPl.rules.removed', { n: d.materials }));
      setRules(null); setNoRule(null); setOther(null);
      await Promise.all([loadRules(), load()]);
    } catch (e) { say(e.message, true); }
    setRuleBusy(false);
  }, [say, loadRules, load]);

  useEffect(() => {
    if (tab !== 'norule') return;
    if (noRule === null) loadNoRule('fert', setNoRule);
    if (other === null) loadNoRule('nonfert', setOther);
  }, [tab, noRule, other, loadNoRule]);

  const ruleGetters = useMemo(
    () => Object.fromEntries(RULE_COLUMNS.map((c) => [c.key, c.get])), [],
  );
  const ruleVisible = useMemo(() => {
    const q = ruleSearch.trim().toLowerCase();
    const base = q
      ? (rules || []).filter((r) => RULE_COLUMNS.some((c) => String(c.get(r) ?? '').toLowerCase().includes(q)))
      : (rules || []);
    const kept = base.filter((r) => rowPassesColumnFilters(r, ruleFilters, ruleGetters));
    return sortRows(kept, ruleSort, ruleGetters);
  }, [rules, ruleSearch, ruleFilters, ruleSort, ruleGetters]);

  // Keyed on `t`: material type's value is a translated word, so a language switch has to re-sort.
  const getters = useMemo(() => Object.fromEntries(COLUMNS.map((c) => [c.key, (r) => c.get(r, t)])), [t]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? mapped.filter((r) => COLUMNS.some((c) => String(c.get(r) || '').toLowerCase().includes(q)))
      : mapped;
    const tagged = untaggedOnly ? base.filter(hasNoTag) : base;
    const filtered = Object.keys(colFilters).length
      ? tagged.filter((r) => rowPassesColumnFilters(r, colFilters, getters))
      : tagged;
    return sortRows(filtered, sort, getters);
  }, [mapped, search, colFilters, sort, getters, untaggedOnly]);
  const untaggedCount = useMemo(() => mapped.filter(hasNoTag).length, [mapped]);

  /* Saving writes the six booleans and patches the row in place, so the table, the "no tag yet"
     count and the summary cards all move together without a refetch of 1,772 rows. */
  const startEdit = (row) => {
    setEditKey(row.sapCode);
    setDraft({
      ...Object.fromEntries(TAG_LIST.map((k) => [k, !!row[k]])),
      newLineCode: row.newLineCode || '',
      newBrandCode: row.newBrandCode || '',
    });
  };
  const cancelEdit = () => { setEditKey(''); setDraft({}); };
  /* Tags, the line and the brand are saved in one press but are not the same kind of thing: a tag
     belongs to the SKU, the line and the brand belong to the STYLE. So the two style-level answers
     go after the tags, and each only when it actually changed — writing a style override on every
     tag edit would leave the table full of overrides that say exactly what the rule already said. */
  const saveTags = async (row) => {
    setSavingTags(true); setError(''); setLineNote('');
    try {
      const res = await fetch(`${API}/tags/${encodeURIComponent(row.sapCode)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(draft),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || t('xPl.ready.saveFailed', { status: res.status }));

      let moved = null;
      if (draft.newLineCode && draft.newLineCode !== (row.newLineCode || '')) {
        const r2 = await fetch(`${API}/line/${encodeURIComponent(row.sapCode)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ newLineCode: draft.newLineCode }),
        });
        const d2 = await r2.json().catch(() => ({}));
        if (!r2.ok) throw new Error(d2.error || t('xPl.ready.lineSaveFailed', { status: r2.status }));
        moved = d2;
      }

      let movedBrand = null;
      if (draft.newBrandCode && draft.newBrandCode !== (row.newBrandCode || '')) {
        const r3 = await fetch(`${API}/brand/${encodeURIComponent(row.sapCode)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ newBrandCode: draft.newBrandCode }),
        });
        const d3 = await r3.json().catch(() => ({}));
        if (!r3.ok) throw new Error(d3.error || t('xPl.ready.brandSaveFailed', { status: r3.status }));
        movedBrand = d3;
      }
      cancelEdit();
      if (moved || movedBrand) {
        /* Both are style-level answers, so they move every SKU of that style — say so instead of
           letting somebody find the other twenty later. A reload is the honest way to show it. */
        const n = (movedBrand || moved).materials;
        const vars = {
          style: row.styleName || row.sapCode,
          line: moved?.newLineCode,
          brand: movedBrand?.newBrandCode,
          n,
        };
        const noteKey = moved && movedBrand ? 'xPl.ready.movedBoth' : (moved ? 'xPl.ready.movedLine' : 'xPl.ready.movedBrand');
        setLineNote(t(noteKey, vars));
        await load();
      } else {
        setMapped((prev) => prev.map((r) => (r.sapCode === row.sapCode ? { ...r, ...draft } : r)));
      }
    } catch (e) { setError(e.message); } finally { setSavingTags(false); }
  };

  const diGetters = useMemo(() => Object.fromEntries(DECIDE_ITEM_COLUMNS.map((c) => [c.key, c.get])), []);
  const diVisible = useMemo(() => {
    const rows = decideItems || [];
    const q = diSearch.trim().toLowerCase();
    const base = q ? rows.filter((r) => DECIDE_ITEM_COLUMNS.some((c) => String(c.get(r) || '').toLowerCase().includes(q))) : rows;
    const filtered = Object.keys(diFilters).length
      ? base.filter((r) => rowPassesColumnFilters(r, diFilters, diGetters))
      : base;
    return sortRows(filtered, diSort, diGetters);
  }, [decideItems, diSearch, diFilters, diSort, diGetters]);

  const nrVisible = useMemo(() => {
    const rows = noRule || [];
    const q = nrSearch.trim().toLowerCase();
    const base = q ? rows.filter((r) => COLUMNS.some((c) => String(c.get(r) || '').toLowerCase().includes(q))) : rows;
    const filtered = Object.keys(nrFilters).length
      ? base.filter((r) => rowPassesColumnFilters(r, nrFilters, getters))
      : base;
    return sortRows(filtered, nrSort, getters);
  }, [noRule, nrSearch, nrFilters, nrSort, getters]);

  const otGetters = useMemo(() => Object.fromEntries(OTHER_COLUMNS.map((c) => [c.key, (r) => c.get(r, t)])), [t]);
  const otVisible = useMemo(() => {
    const rows = other || [];
    const q = otSearch.trim().toLowerCase();
    const base = q ? rows.filter((r) => OTHER_COLUMNS.some((c) => String(c.get(r) || '').toLowerCase().includes(q))) : rows;
    const filtered = Object.keys(otFilters).length
      ? base.filter((r) => rowPassesColumnFilters(r, otFilters, otGetters))
      : base;
    return sortRows(filtered, otSort, otGetters);
  }, [other, otSearch, otFilters, otSort, otGetters]);

  const dGetters = useMemo(() => Object.fromEntries(DECISION_COLUMNS.map((c) => [c.key, c.get])), []);
  const dVisible = useMemo(() => {
    const filtered = Object.keys(dFilters).length
      ? decisions.filter((r) => rowPassesColumnFilters(r, dFilters, dGetters))
      : decisions;
    return sortRows(filtered, dSort, dGetters);
  }, [decisions, dFilters, dSort, dGetters]);

  /* Export what is on screen, filters and sort included — the point of the filters is to cut the file
     down, and a button that ignored them would be a trap. */
  /* Writes a style override and reloads, so the row leaves the list and the counters move in the
     same breath — the point of answering it here rather than in a spreadsheet. */
  const saveDecision = async (row) => {
    const key = decisionKey(row);
    const pick = decidePick[key];
    if (!pick) return;
    setDecideBusy(key); setError(''); setDecideDone('');
    try {
      const res = await fetch(`${API}/decide`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          styleName: row.style_name,
          brandCode: row.brand_code,
          oldLineCode: row.old_line_code,
          categoryCode: row.category_code,
          subCategory: row.sub_category,
          newLineCode: pick,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('xPl.open.saveFailed'));
      setDecideDone(t('xPl.open.decided', { style: row.style_name, line: pick, n: data.materials }));
      setDecidePick((prev) => { const next = { ...prev }; delete next[key]; return next; });
      await Promise.all([load(), loadDecideItems()]);
    } catch (err) { setError(err.message); } finally { setDecideBusy(null); }
  };

  const exportExcel = async (which = 'mapped') => {
    // One description per table, so a new table cannot pick up another one's sheet name by accident.
    const specs = {
      /* The file name stays English (it is a path); the title, sheet name and headings are read by a
         person and follow the screen's language — nothing reads this workbook back in. */
      mapped: { rows: visible, all: mapped, cols: COLUMNS, search, file: 'Mapped',
                label: t('xPl.exp.labelMapped'), sheet: t('xPl.exp.sheetMapped') },
      norule: { rows: nrVisible, all: noRule || [], cols: COLUMNS, search: nrSearch, file: 'NoRule',
                label: t('xPl.exp.labelNorule'), sheet: t('xPl.exp.sheetNorule') },
      other: { rows: otVisible, all: other || [], cols: OTHER_COLUMNS, search: otSearch, file: 'NoRule_OtherTypes',
               label: t('xPl.exp.labelOther'), sheet: t('xPl.exp.sheetOther') },
      decisions: { rows: dVisible, all: decisions, cols: DECISION_COLUMNS, search: '', file: 'Decisions',
                   label: t('xPl.exp.labelDecisions'), sheet: t('xPl.exp.sheetDecisions') },
      decideItems: { rows: diVisible, all: decideItems || [], cols: DECIDE_ITEM_COLUMNS, search: diSearch,
                     file: 'Decisions_Items', label: t('xPl.exp.labelDecideItems'),
                     sheet: t('xPl.exp.sheetDecideItems') },
    };
    const spec = specs[which] || specs.mapped;
    const { rows, cols, label } = spec;
    const total = spec.all.length;
    setBusy(true); setError('');
    try {
      const stamp = fmtDateTimeVN(new Date(), '');
      const filename = `HEVN_Product_Line_${spec.file}_${stamp.replace(/[: ]/g, '-')}.xlsx`;
      const res = await fetch(XLSX_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          filename,
          title: t('xPl.exp.title', { label }),
          generated: stamp,
          sheetName: spec.sheet,
          filters: [
            t('xPl.exp.rowsOf', { n: rows.length, total }),
            spec.search ? t('xPl.exp.search', { q: spec.search }) : '',
          ].filter(Boolean),
          columns: cols.map((c) => t(c.labelKey)),
          widths: cols.map((c) => c.w),
          statusColumn: 0,
          freezeColumns: 3,
          // The endpoint reads each row as { values: [...] }; a bare array produced empty rows.
          rows: rows.map((r) => ({ values: cols.map((c) => c.get(r, t)) })),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || t('xPl.exp.failed', { status: res.status }));
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const st = summary?.status || {};
  const toolsOn = !!sort.key || !!Object.keys(colFilters).length || !!search.trim();

  return (
    <div>
      <Header />
      <div className="content">
        <div className="sp-page pl-page">
          <div className="mh-titlerow">
            <div className="sp-title">
              <h2>{t('xPl.title')}</h2>
              <p>{t('xPl.subtitle')}</p>
            </div>
            <ModuleHelp steps={TOUR} tourKey="productLine" ready={!loading} onBeforeTour={() => setTab('ready')} />
          </div>

          <section className="sp-section">
            <div className="sp-section-head">
              <div><Database size={14} /> {t('xPl.whereStands')}</div>
              <button type="button" className="sp-btn" onClick={load}><RefreshCw size={13} /> {t('xPl.refresh')}</button>
            </div>
            {/* THE BRAND MOVES TOO, and this line used to say it never did (Miley via Max,
                2026-10-02). It was true when the screen only replaced the line segment; since the
                brand became part of the answer, new_code is built as resolved brand + new line +
                the category and sub-category carried over from the old code. Measured the day this
                was corrected: 304 of 9,074 materials sit on a different brand, all 11 -> 41, and
                none of them was set by hand — the brand follows the line it is moved onto. The two
                claims that are still true are left standing: nothing is written back (every write
                lands in 00_PORTAL_pl_*), and no other screen reads the new code. */}
            <p className="sp-sub">
              <Trans t={t} i18nKey="xPl.mappingNote" components={{ b: <b /> }} />
            </p>
            {error && <p className="sp-error"><AlertTriangle size={13} /> {error}</p>}
            {summary && (
              <div className="sp-stat-row">
                <Stat label={t('xPl.stat.mapped')} value={st.mapped?.materials} tone="ok" sub={t('xPl.stat.sellableSub', { n: (st.mapped?.fert ?? 0).toLocaleString('en-GB') })} />
                <Stat label={t('xPl.stat.needsDecision')} value={st.needs_style_decision?.materials} tone="warn" sub={t('xPl.stat.questionsSub', { n: decisions.length })} />
                {/* ZERO IS AN ANSWER AND A DASH IS NOT — the summary groups by status, so once
                    the last rule was written the `unmapped_code` key stopped existing and the card
                    read "—", which says "no information" about a number that is known to be none.
                    Seen the morning the HXW rules cleared the tab. */}
                <Stat label={t('xPl.stat.noRule')} value={st.unmapped_code?.materials ?? 0} sub={t('xPl.stat.sellableSub', { n: (st.unmapped_code?.fert ?? 0).toLocaleString('en-GB') })} />
                <Stat label={t('xPl.stat.rules')} value={summary.rules?.total} sub={t('xPl.stat.ambiguousSub', { n: summary.rules?.ambiguous })} />
                <Stat label={t('xPl.stat.skusTagged')} value={summary.tags?.tagged} sub={t('xPl.stat.ofSub', { n: (summary.tags?.skus ?? 0).toLocaleString('en-GB') })} />
              </div>
            )}
          </section>

          <div className="sp-tabs view-tabs" role="tablist">
            <button type="button" className={`sp-tab ${tab === 'ready' ? 'active' : ''}`} onClick={() => setTab('ready')}>
              <TabIcon k="ready" />{t('xPl.tab.ready', { n: mapped.length.toLocaleString('en-GB') })}
            </button>
            <button type="button" className={`sp-tab ${tab === 'open' ? 'active' : ''}`} onClick={() => setTab('open')}>
              <TabIcon k="open" />{t('xPl.tab.open', { n: decisions.length })}
            </button>
            <button type="button" className={`sp-tab ${tab === 'norule' ? 'active' : ''}`} onClick={() => setTab('norule')}>
              <TabIcon k="norule" />{/* THE BADGE COUNTED SELLABLE AND THE TAB LISTS EVERYTHING — Miley, 2026-10-06,
                  looking at "No rule yet (4)" above a page holding 26 rows. It counted FERT only
                  while the tab draws the 4 sellable AND the 22 others, so the number promised a
                  quarter of what is inside. It counts what the tab shows, and the sellable figure
                  is kept beside it, since that is the half somebody has to answer first. */}
              {t('xPl.tab.norule', { n: (st.unmapped_code?.materials ?? 0).toLocaleString('en-GB') })}
            </button>
            {/* THE RULES, where they can be changed — Miley, 2026-10-06. Last, because it is the
                layer BEHIND the three tabs beside it: the other three show materials, this shows
                what decides them. */}
            <button type="button" className={`sp-tab ${tab === 'rules' ? 'active' : ''}`} onClick={() => setTab('rules')}>
              <TabIcon k="rules" />{t('xPl.tab.rules', { n: (summary?.rules?.total ?? 0).toLocaleString('en-GB') })}
            </button>
          </div>

          {tab === 'ready' && (
            <section className="sp-section">
              <div className="sp-section-head">
                <div>{t('xPl.ready.head')}</div>
                <button type="button" className="sp-btn" disabled={busy || !visible.length} onClick={() => exportExcel('mapped')}>
                  <Download size={13} /> {busy ? t('xPl.building') : t('xPl.exportExcel')}
                </button>
              </div>
              {/* The brand is editable here as well, and saying only "line" sent people looking for a
                  control that was already in front of them. */}
              <p className="sp-sub">
                <Trans t={t} i18nKey="xPl.ready.note" components={{ b: <b /> }} />
              </p>
              <div className="sp-toolbar">
                <input
                  className="sp-input"
                  style={{ minWidth: 300 }}
                  placeholder={t('xPl.searchAny')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <label className="pl-check">
                  <input type="checkbox" checked={untaggedOnly}
                         onChange={(e) => { setUntaggedOnly(e.target.checked); cancelEdit(); }} />
                  <span>{t('xPl.ready.untaggedOnly', { n: untaggedCount.toLocaleString('en-GB') })}</span>
                </label>
                {toolsOn && (
                  <button type="button" className="sp-btn"
                          onClick={() => { setSearch(''); setColFilters({}); setSort({ key: '', dir: 'asc' }); }}>
                    {t('xPl.clearTools')}
                  </button>
                )}
                <span className="sp-sub">
                  {visible.length !== mapped.length
                    ? t('xPl.rowsOf', { n: visible.length.toLocaleString('en-GB'), total: mapped.length.toLocaleString('en-GB') })
                    : t('xPl.rowsN', { n: visible.length.toLocaleString('en-GB') })}
                </span>
              </div>
              {!!lineNote && <div className="sp-alert ok">{t('xPl.ready.lineChanged', { note: lineNote })}</div>}
              {loading ? <p className="sp-sub">{t('xPl.loading')}</p> : (
                <DataTable
                  columns={COLUMNS} rows={visible} allRows={mapped}
                  sort={sort} setSort={setSort}
                  colFilters={colFilters} setColFilters={setColFilters}
                  openFilter={openFilter} setOpenFilter={setOpenFilter}
                  keyOf={(r) => r.sapCode}
                  /* Max, 2026-09-07: "thêm cột No giúp tôi và vẫn freeze đến cột SAP code nhé" —
                     so the pinned run is No. + the three that identify a row. */
                  numbered
                  frozen={FROZEN + 1}
                  tagEdit={{
                    lines,
                    brands,
                    canEdit: mayEditTags,
                    editingKey: editKey,
                    draft,
                    setDraft,
                    saving: savingTags,
                    onEdit: startEdit,
                    onCancel: cancelEdit,
                    onSave: saveTags,
                  }}
                />
              )}
            </section>
          )}

          {tab === 'open' && (
            <>
              <section className="sp-section">
                <div className="sp-section-head">
                  <div>{t('xPl.open.head', { n: decisions.length })}</div>
                  <button type="button" className="sp-btn" disabled={busy || !dVisible.length}
                          onClick={() => exportExcel('decisions')}>
                    <Download size={13} /> {busy ? t('xPl.building') : t('xPl.exportExcel')}
                  </button>
                </div>
                {!!decideDone && <div className="sp-alert ok">{t('xPl.open.answered', { text: decideDone })}</div>}
                <p className="sp-sub">{t('xPl.open.note')}</p>
                <DataTable
                  columns={DECISION_COLUMNS} rows={dVisible} allRows={decisions}
                  sort={dSort} setSort={setDSort}
                  colFilters={dFilters} setColFilters={setDFilters}
                  openFilter={dOpen} setOpenFilter={setDOpen}
                  keyOf={(r) => `${r.style_name}-${r.brand_code}-${r.category_code}-${r.sub_category}`}
                  frozen={2}
                  action={{
                    label: t('xPl.open.answer'),
                    width: 230,
                    render: (r) => {
                      const key = decisionKey(r);
                      /* Only this rule's own candidates. Anything else would classify the style
                         somewhere the source workbook never proposed, and the server refuses it
                         anyway — offering it here would just be a dead end with a nicer font. */
                      const options = String(r.candidates || '').split(',').map((x) => x.trim()).filter(Boolean);
                      return (
                        <div className="pl-decide">
                          <select
                            className="sp-input"
                            value={decidePick[key] || ''}
                            disabled={!mayEditTags || decideBusy === key}
                            onChange={(e) => setDecidePick((prev) => ({ ...prev, [key]: e.target.value }))}
                          >
                            <option value="">{t('xPl.open.pick')}</option>
                            {/* The code alone: it is what the team says out loud, it is what the
                                Candidates column beside it already shows, and the names live in a
                                table this screen does not load. */}
                            {options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                          <button
                            type="button"
                            className="sp-btn primary"
                            disabled={!mayEditTags || !decidePick[key] || decideBusy === key}
                            title={mayEditTags ? t('xPl.open.classifyTitle') : t('xPl.open.noAnswerRights')}
                            onClick={() => saveDecision(r)}
                          >
                            {decideBusy === key ? t('xPl.saving') : t('xPl.save')}
                          </button>
                        </div>
                      );
                    },
                  }}
                />
              </section>

              <section className="sp-section">
                <div className="sp-section-head">
                  <div>{t('xPl.open.itemsHead', { n: (decideItems || []).length.toLocaleString('en-GB') })}</div>
                  <button type="button" className="sp-btn" disabled={busy || !diVisible.length}
                          onClick={() => exportExcel('decideItems')}>
                    <Download size={13} /> {busy ? t('xPl.building') : t('xPl.exportExcel')}
                  </button>
                </div>
                <p className="sp-sub">{t('xPl.open.itemsNote')}</p>
                <div className="sp-toolbar">
                  <input
                    className="sp-input"
                    style={{ minWidth: 300 }}
                    placeholder={t('xPl.searchAny')}
                    value={diSearch}
                    onChange={(e) => setDiSearch(e.target.value)}
                  />
                  {(!!diSearch.trim() || !!Object.keys(diFilters).length || !!diSort.key) && (
                    <button type="button" className="sp-btn"
                            onClick={() => { setDiSearch(''); setDiFilters({}); setDiSort({ key: '', dir: 'asc' }); }}>
                      {t('xPl.clearTools')}
                    </button>
                  )}
                  <span className="sp-sub">
                    {diVisible.length !== (decideItems || []).length
                      ? t('xPl.rowsOf', { n: diVisible.length.toLocaleString('en-GB'), total: (decideItems || []).length.toLocaleString('en-GB') })
                      : t('xPl.rowsN', { n: diVisible.length.toLocaleString('en-GB') })}
                  </span>
                </div>
                {decideItems === null ? <p className="sp-sub">{t('xPl.loading')}</p> : (
                  <DataTable
                    columns={DECIDE_ITEM_COLUMNS} rows={diVisible} allRows={decideItems}
                    sort={diSort} setSort={setDiSort}
                    colFilters={diFilters} setColFilters={setDiFilters}
                    openFilter={diOpen} setOpenFilter={setDiOpen}
                    keyOf={(r) => r.sapCode}
                    frozen={FROZEN}
                  />
                )}
              </section>

            </>
          )}

          {tab === 'norule' && (
            <>
              <section className="sp-section">
                <div className="sp-section-head"><div>{t('xPl.norule.groupedHead')}</div></div>
                <p className="sp-sub">{t('xPl.norule.groupedNote')}</p>
                <div className="pl-scroll">
                  <table className="pl-table">
                    <thead>
                      <tr><th>{t('xPl.norule.colBrandLine')}</th><th className="num">{t('xPl.norule.colCodes')}</th>
                        <th className="num">{t('xPl.col.materials')}</th>
                        <th className="num">{t('xPl.col.sellable')}</th><th>{t('xPl.col.example')}</th></tr>
                    </thead>
                    <tbody>
                      {unmapped.map((r) => (
                        <tr key={`${r.brand_code}-${r.old_line_code}`}>
                          <td className="mono">{r.brand_code} / {r.old_line_code}</td>
                          <td className="num">{r.codes}</td>
                          <td className="num">{r.materials.toLocaleString('en-GB')}</td>
                          <td className="num">{r.fert.toLocaleString('en-GB')}</td>
                          <td className="mono">{r.sample_code} · {r.sample_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="sp-section">
                <div className="sp-section-head">
                  <div>{t('xPl.norule.sellableHead', { n: (noRule || []).length.toLocaleString('en-GB') })}</div>
                  <button type="button" className="sp-btn" disabled={busy || !nrVisible.length} onClick={() => exportExcel('norule')}>
                    <Download size={13} /> {busy ? t('xPl.building') : t('xPl.exportExcel')}
                  </button>
                </div>
                <p className="sp-sub">{t('xPl.norule.sellableNote')}</p>
                <div className="sp-toolbar">
                  <input
                    className="sp-input"
                    style={{ minWidth: 300 }}
                    placeholder={t('xPl.searchAny')}
                    value={nrSearch}
                    onChange={(e) => setNrSearch(e.target.value)}
                  />
                  {(!!nrSearch.trim() || !!Object.keys(nrFilters).length || !!nrSort.key) && (
                    <button type="button" className="sp-btn"
                            onClick={() => { setNrSearch(''); setNrFilters({}); setNrSort({ key: '', dir: 'asc' }); }}>
                      {t('xPl.clearTools')}
                    </button>
                  )}
                  <span className="sp-sub">
                    {nrVisible.length !== (noRule || []).length
                      ? t('xPl.rowsOf', { n: nrVisible.length.toLocaleString('en-GB'), total: (noRule || []).length.toLocaleString('en-GB') })
                      : t('xPl.rowsN', { n: nrVisible.length.toLocaleString('en-GB') })}
                  </span>
                </div>
                {noRule === null ? <p className="sp-sub">{t('xPl.loading')}</p> : (
                  <DataTable
                    columns={COLUMNS} rows={nrVisible} allRows={noRule}
                    sort={nrSort} setSort={setNrSort}
                    colFilters={nrFilters} setColFilters={setNrFilters}
                    openFilter={nrOpen} setOpenFilter={setNrOpen}
                    keyOf={(r) => r.sapCode}
                    frozen={FROZEN}
                  />
                )}
              </section>

              <section className="sp-section">
                <div className="sp-section-head">
                  <div>{t('xPl.norule.otherHead', { n: (other || []).length.toLocaleString('en-GB') })}</div>
                  <button type="button" className="sp-btn" disabled={busy || !otVisible.length} onClick={() => exportExcel('other')}>
                    <Download size={13} /> {busy ? t('xPl.building') : t('xPl.exportExcel')}
                  </button>
                </div>
                <p className="sp-sub">
                  <Trans t={t} i18nKey="xPl.norule.otherNote" components={{ b: <b /> }} />
                </p>
                <div className="sp-toolbar">
                  <input
                    className="sp-input"
                    style={{ minWidth: 300 }}
                    placeholder={t('xPl.searchAny')}
                    value={otSearch}
                    onChange={(e) => setOtSearch(e.target.value)}
                  />
                  {(!!otSearch.trim() || !!Object.keys(otFilters).length || !!otSort.key) && (
                    <button type="button" className="sp-btn"
                            onClick={() => { setOtSearch(''); setOtFilters({}); setOtSort({ key: '', dir: 'asc' }); }}>
                      {t('xPl.clearTools')}
                    </button>
                  )}
                  <span className="sp-sub">
                    {otVisible.length !== (other || []).length
                      ? t('xPl.rowsOf', { n: otVisible.length.toLocaleString('en-GB'), total: (other || []).length.toLocaleString('en-GB') })
                      : t('xPl.rowsN', { n: otVisible.length.toLocaleString('en-GB') })}
                  </span>
                </div>
                {other === null ? <p className="sp-sub">{t('xPl.loading')}</p> : (
                  <DataTable
                    columns={OTHER_COLUMNS} rows={otVisible} allRows={other}
                    sort={otSort} setSort={setOtSort}
                    colFilters={otFilters} setColFilters={setOtFilters}
                    openFilter={otOpen} setOpenFilter={setOtOpen}
                    keyOf={(r) => r.sapCode}
                    frozen={FROZEN}
                  />
                )}
              </section>
            </>
          )}

          {/* THE RULES — Miley via Max, 2026-10-06. Correcting a ROW leaves the rule still wrong,
              so the same question comes back on the next import. This is the layer that decides
              the other three tabs, and every line and brand is on the menu rather than only the
              candidates the source workbook happened to offer: she is writing the rule. */}
          {tab === 'rules' && (
            <section className="sp-section">
              <div className="sp-section-head">
                <h3>{t('xPl.rules.head')}</h3>
                <button type="button" className="sp-btn"
                  onClick={() => { setRuleAdd({ brandCode: '', oldLineCode: '', categoryCode: '', subCategory: '', newLineCode: '', newBrandCode: '', note: '' }); setRuleEditId(null); }}>
                  <Plus size={13} /> {t('xPl.rules.add')}
                </button>
              </div>
              <p className="sp-note">
                <Trans t={t} i18nKey="xPl.rules.note" components={{ b: <b /> }} />
              </p>
              {ruleMsg && (
                <p className={ruleMsg.bad ? 'sp-error' : 'sp-ok'}>
                  {ruleMsg.bad && <AlertTriangle size={13} />} {ruleMsg.text}
                </p>
              )}

              {ruleAdd && (
                <div className="pl-rule-add">
                  {[['brandCode', t('xPl.rules.fBrandCode')], ['oldLineCode', t('xPl.rules.fOldLine')],
                    ['categoryCode', t('xPl.rules.fCategory')], ['subCategory', t('xPl.rules.fSubCategory')]].map(([k, label]) => (
                      <label key={k}>
                        <span>{label}</span>
                        <input
                          value={ruleAdd[k]}
                          onChange={(e) => setRuleAdd({ ...ruleAdd, [k]: e.target.value })}
                          /* 'Empty' is not a word to translate: it is the literal value the rule table stores
                             for "no sub-category", and the hint shows what to type. */
                          placeholder={k === 'subCategory' ? 'Empty' : ''}
                        />
                      </label>
                  ))}
                  <label>
                    <span>{t('xPl.rules.fNewLine')}</span>
                    {/* EVERY ACTIVE LINE, not the candidates — that was the ask. */}
                    <select value={ruleAdd.newLineCode}
                      onChange={(e) => setRuleAdd({ ...ruleAdd, newLineCode: e.target.value })}>
                      <option value="">—</option>
                      {lines.map((l) => <option key={l.code} value={l.code}>{l.code} — {l.name}</option>)}
                    </select>
                  </label>
                  <label>
                    <span>{t('xPl.rules.fNewBrand')}</span>
                    {/* The dash is not a placeholder: it is the setting that leaves the brand to
                        the line, which is what 328 of the 328 rules do today. */}
                    <select value={ruleAdd.newBrandCode}
                      onChange={(e) => setRuleAdd({ ...ruleAdd, newBrandCode: e.target.value })}>
                      <option value="">{t('xPl.rules.followLine')}</option>
                      {brands.map((b) => <option key={b.code} value={b.code}>{b.code} — {b.name}</option>)}
                    </select>
                  </label>
                  <label className="wide">
                    <span>{t('xPl.rules.fNote')}</span>
                    <input value={ruleAdd.note}
                      onChange={(e) => setRuleAdd({ ...ruleAdd, note: e.target.value })} />
                  </label>
                  <div className="pl-rule-add-act">
                    <button type="button" className="sp-btn primary" disabled={ruleBusy}
                      onClick={() => saveRule(ruleAdd)}>{t('xPl.save')}</button>
                    <button type="button" className="sp-btn" onClick={() => setRuleAdd(null)}>{t('xPl.cancel')}</button>
                  </div>
                </div>
              )}

              <div className="sp-table-tools">
                <input className="sp-search" placeholder={t('xPl.searchAny')}
                  value={ruleSearch} onChange={(e) => setRuleSearch(e.target.value)} />
                {/* THE WAY BACK OUT — Max, 2026-10-06: "no rule ko có nút clear sort và filter của
                    bảng á". Every other table on this screen carries one and this table did not,
                    so a column funnel left on had no undo but hunting for which header was lit:
                    it reads "9 of 328 rules" and nothing on screen says which of fourteen columns
                    is doing it. Drawn only when there IS something to clear, like the others. */}
                {(!!ruleSearch.trim() || !!Object.keys(ruleFilters).length || !!ruleSort.key) && (
                  <button type="button" className="sp-btn"
                          onClick={() => {
                            setRuleSearch(''); setRuleFilters({});
                            setRuleSort({ key: '', dir: 'asc' });
                          }}>
                    {t('xPl.clearTools')}
                  </button>
                )}
                <span className="sp-count">{t('xPl.rules.count', { n: ruleVisible.length.toLocaleString('en-GB'), total: (rules || []).length.toLocaleString('en-GB') })}</span>
              </div>
              <DataTable
                columns={RULE_COLUMNS}
                rows={ruleVisible}
                allRows={rules || []}
                sort={ruleSort} setSort={setRuleSort}
                colFilters={ruleFilters} setColFilters={setRuleFilters}
                openFilter={ruleOpen} setOpenFilter={setRuleOpen}
                keyOf={(r) => r.ruleId}
                frozen={0}
                action={{
                  label: t('xPl.action'),
                  /* Sized by the widest thing it draws, which is now TWO pickers and two buttons.
                     This column clips rather than wraps, so a width left at 230 hangs the Cancel
                     button outside the frame — the trap this file already records, and 360 was
                     still 73px short when it was measured. 400, with the brand picker capped
                     narrower than the line one in the stylesheet because it shows "41 — TERG"
                     where the other shows "OUL — Outdoor Chair — UL". */
                  width: 400,
                  render: (r) => (ruleEditId === r.ruleId ? (
                    <span className="pl-rule-edit">
                      <select value={ruleDraftLine} onChange={(e) => setRuleDraftLine(e.target.value)}>
                        <option value="">—</option>
                        {lines.map((l) => <option key={l.code} value={l.code}>{l.code} — {l.name}</option>)}
                      </select>
                      <select value={ruleDraftBrand} title={t('xPl.rules.fNewBrand')}
                        className="pl-rule-brand"
                        onChange={(e) => setRuleDraftBrand(e.target.value)}>
                        <option value="">{t('xPl.rules.brandFollowsLine')}</option>
                        {brands.map((b) => <option key={b.code} value={b.code}>{b.code} — {b.name}</option>)}
                      </select>
                      {/* SAVEABLE WITH THE BRAND ALONE — 37 rules are ambiguous and have no line
                          to offer, and giving one of those a brand must not mean answering the
                          line question by accident. Disabled only when neither has been set. */}
                      <button type="button" className="sp-btn primary"
                        disabled={ruleBusy || (!ruleDraftLine && !ruleDraftBrand)}
                        onClick={() => saveRule({
                          brandCode: r.brandCode, oldLineCode: r.oldLineCode,
                          categoryCode: r.categoryCode, subCategory: r.subCategory,
                          newLineCode: ruleDraftLine, newBrandCode: ruleDraftBrand, note: r.note,
                        })}>{t('xPl.save')}</button>
                      <button type="button" className="sp-btn" onClick={() => setRuleEditId(null)}>{t('xPl.cancel')}</button>
                    </span>
                  ) : (
                    <span className="pl-rule-edit">
                      <button type="button" className="sp-btn"
                        onClick={() => { setRuleEditId(r.ruleId); setRuleDraftLine(r.newLineCode || ''); setRuleDraftBrand(r.newBrandCode || ''); setRuleAdd(null); }}>
                        <Pencil size={12} /> {t('xPl.edit')}
                      </button>
                      <button type="button" className="sp-btn" title={t('xPl.rules.removeTitle')}
                        disabled={ruleBusy} onClick={() => dropRule(r)}>
                        <Trash2 size={12} />
                      </button>
                    </span>
                  )),
                }}
              />
            </section>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProductLine;
